<template>
    <div class="reports-landing">
        <div class="o-main-content-area">
            <div class="o-page-top-heading c-responsive-fixwidth">
                <h1 class="h1">Reports</h1>
                <p class="reports-landing__lede">
                    Pick a report.
                </p>
            </div>

            <section class="reports-landing__section">
                <div class="reports-landing__grid">
                    <router-link
                        v-for="tile in tiles"
                        :key="tile.slug"
                        :to="{ path: '/report-list/appsmith', query: { page: tile.page } }"
                        class="reports-landing__tile reports-landing__tile--live"
                    >
                        <span class="reports-landing__icon">
                            <i :class="['fa', tile.icon]" aria-hidden="true"></i>
                        </span>
                        <span class="reports-landing__title">{{ tile.title }}</span>
                        <span class="reports-landing__desc">{{ tile.description }}</span>
                    </router-link>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
// Source of truth for the unified report menu. Every tile opens an AppSmith
// page (app `cs-reports (2)`, branch=main) embedded by reportsAppSmith.vue at
// /report-list/appsmith?page=<slug>-<pageId>. Reports are AppSmith-only — do
// NOT route a tile to a native Wijmo grid (see #1502). To add a report, add a
// page in cs-reports-2 and a tile here with its <slug>-<pageId> token.
const TILES = [
    {
        slug: 'monthly-jobs',
        title: 'Monthly / Yearly Job Details',
        description: 'Job summary across a date range with call counts.',
        icon: 'fa-bar-chart',
        page: 'p-monthlyjob-69f896f4cf810d7e7c045f50',
    },
    {
        slug: 'employee-export',
        title: 'Employee Export',
        description: 'Full employee snapshot with contact and status fields.',
        icon: 'fa-users',
        page: 'p-employeeexport-69f896f4cf810d7e7c045f52',
    },
    {
        slug: 'employer-export',
        title: 'Employer Export',
        description: 'Employer directory with admin contacts and other users.',
        icon: 'fa-building-o',
        page: 'p-employerexport-69f896f4cf810d7e7c045f4a',
    },
    {
        slug: 'employer-shift-count',
        title: 'Employer Shift Count',
        description: 'Shifts worked per employer over a date range.',
        icon: 'fa-calendar',
        page: 'p-employershiftcount-69f896f5cf810d7e7c045f54',
    },
    {
        slug: 'last-confirmed-shift',
        title: 'Last Confirmed Shift',
        description: 'Most recent confirmed shift per active employee.',
        icon: 'fa-calendar-check-o',
        page: 'p-lastconfirmedshift-69f896f4cf810d7e7c045f4d',
    },
    {
        slug: 'user-total-hours',
        title: 'User Total Hours',
        description: 'Confirmed calls and hours worked per employee.',
        icon: 'fa-hourglass',
        page: 'p-usertotalhoursconfirmed-69f896f4cf810d7e7c045f47',
    },
    {
        slug: 'monthly-jobs-chart',
        title: 'Monthly / Yearly Job Details Chart',
        description: 'Visual chart of job and call volume over time.',
        icon: 'fa-line-chart',
        page: 'p-monthlyjobchart-69f896f4cf810d7e7c045f51',
    },
    {
        slug: 'attendance',
        title: 'Attendance',
        description: 'Per-employee attendance roll-up across a date range.',
        icon: 'fa-check-square-o',
        page: 'p-attendance-69f896f4cf810d7e7c045f4c',
    },
    {
        slug: 'attendance-chart',
        title: 'Attendance Chart',
        description: 'Visual chart of attendance trends.',
        icon: 'fa-area-chart',
        page: 'p-attendancechart-69f896f5cf810d7e7c045f55',
    },
    {
        slug: 'call-user-skill-hours',
        title: 'Call / User / Skill / Hours',
        description: 'Cross-tab of calls, users, skills, and hours.',
        icon: 'fa-sitemap',
        page: 'p-calluserskillhours-69f896f4cf810d7e7c045f48',
    },
    {
        slug: 'nlrb',
        title: 'NLRB Report',
        description: 'NLRB compliance export.',
        icon: 'fa-file-text-o',
        page: 'p-nlrb-69f896f5cf810d7e7c045f53',
    },
    {
        slug: 'action-log-search',
        title: 'Action Log Search',
        description: 'Audit trail search across user actions.',
        icon: 'fa-list-alt',
        page: 'p-actionlogsearch-69f896f4cf810d7e7c045f49',
    },
    {
        slug: 'notice-log-search',
        title: 'Notice Log Search',
        description: 'Search outgoing SMS, email, and push notice logs.',
        icon: 'fa-bell-o',
        page: 'p-noticelogsearch-69f896f4cf810d7e7c045f4f',
    },
    {
        slug: 'failed-sms',
        title: 'Failed SMS',
        description: 'SMS delivery failures with carrier reasons.',
        icon: 'fa-comment-o',
        page: 'p-failedsms-69f896f5cf810d7e7c045f56',
    },
    {
        slug: 'unsubscribed-email',
        title: 'Unsubscribed / Failed Email',
        description: 'Email opt-outs and delivery failures.',
        icon: 'fa-envelope-o',
        page: 'p-unsubscribedemail-69f896f4cf810d7e7c045f4e',
    },
    {
        slug: 'employees-and-skills',
        title: 'Employees and Skills',
        description: 'Crew × skill catalog matrix. Cell / Email / Status optional; compact view available.',
        icon: 'fa-th',
        page: 'p-employeesandskills-6a185e4084ff792684e8077b',
    },
    {
        slug: 'new-hire-engagement',
        title: 'New-Hire Engagement',
        description: 'Onboarding progress and engagement for recent new hires.',
        icon: 'fa-user-plus',
        page: 'p-newhireengagement-6a20371e84ff792684e80b75',
    },
    {
        slug: 'multi-year-call-trend',
        title: 'Multi-Year Call Trend',
        description: 'Year-over-year confirmed call volume, one line per year.',
        icon: 'fa-line-chart',
        page: 'p-multiyearjobtrend-6a274d2c7f5c01435149922e',
    },
    {
        slug: 'card-check',
        title: 'Card Check',
        description: 'Employee cohort by employer + date range — shifts, hours, days, contact, member flag.',
        icon: 'fa-id-card-o',
        page: 'p-cardcheck-6a2ad9c17f5c014351499601',
    },
];

export default {
    name: 'ReportsLanding',
    computed: {
        tiles() { return TILES; },
    },
};
</script>

<style scoped>
.reports-landing {
    padding-top: 60px;
    padding-left: 20px;
}
.reports-landing__lede {
    color: #6b6b6b;
    margin: 4px 0 16px;
}
.reports-landing__section { margin-top: 24px; }
.reports-landing__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 14px;
    margin-right: 20px;
}
.reports-landing__tile {
    position: relative;
    display: flex;
    flex-direction: column;
    padding: 16px 18px;
    border-radius: 6px;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    border: 1px solid #e6e6e6;
    text-decoration: none;
    color: inherit;
    transition: box-shadow 0.15s ease, transform 0.15s ease;
}
.reports-landing__tile--live {
    cursor: pointer;
}
.reports-landing__tile--live:hover {
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
}
.reports-landing__icon {
    font-size: 1.5rem;
    color: #2a6ebd;
    margin-bottom: 8px;
}
.reports-landing__title {
    font-weight: 600;
    color: #333;
    margin-bottom: 4px;
}
.reports-landing__desc {
    font-size: 0.85rem;
    color: #6b6b6b;
    line-height: 1.35;
}
</style>
